// src/sectors/pilates/reports/staff/components/StaffMembersAccordion.jsx
import React, { useMemo, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

/* ---------------- helpers (aynı dil) ---------------- */
const SUMMARY_COLS = [
  { w: 220 }, // Üye
  { w: 110 }, // Paket
  { w: 92 }, // Seans
  { w: 150 }, // Yoklama
  { w: 110 }, // Ödenen
  { w: 110 }, // Borç
  { w: 180 }, // Son Seans
  { w: 120 }, // Üye Beyanı
  { w: 180 }, // Not / Info
  { w: 46 }, // expand boşluğu
];

function ColGroup({ cols }) {
  return (
    <colgroup>
      {cols.map((c, i) => (
        <col key={i} style={{ width: c.w }} />
      ))}
    </colgroup>
  );
}

function fmtDateTR(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('tr-TR');
}
function fmtTimeTR(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
}
function safeText(v, fallback = '—') {
  const s = (v ?? '').toString().trim();
  return s ? s : fallback;
}
function short(v, n = 22) {
  const s = safeText(v, '');
  if (!s) return '—';
  return s.length > n ? `${s.slice(0, n)}…` : s;
}
function fmtTry(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toLocaleString('tr-TR')} ₺`;
}
function attendanceChip(status) {
  const v = String(status ?? '').toUpperCase();
  if (v === 'ATTENDED') return { label: 'Katıldı', color: 'success' };
  if (v === 'NO_SHOW') return { label: 'Gelmedi', color: 'error' };
  if (v === 'CANCELLED') return { label: 'İptal', color: 'warning' };
  if (v === 'PENDING') return { label: 'Bekliyor', color: 'default' };
  if (v) return { label: v, color: 'default' };
  return { label: '—', color: 'default' };
}
function paymentLabel(v) {
  const s = String(v ?? '').toUpperCase();
  if (s === 'CASH') return 'Nakit';
  if (s === 'CARD') return 'Kart';
  if (s === 'TRANSFER') return 'Havale';
  if (s === 'PACKAGE') return 'Paket';
  return s || '—';
}
function usageLabel(r) {
  const used = Number(r?.packageUsedSessionIndex);
  const total = Number(r?.packageTotalSession);
  if (Number.isFinite(used) && Number.isFinite(total) && total > 0) return `${used} / ${total}`;
  if (Number.isFinite(used)) return String(used);
  return null;
}
function usageColor(r) {
  const used = Number(r?.packageUsedSessionIndex);
  const total = Number(r?.packageTotalSession);
  if (!Number.isFinite(used) || !Number.isFinite(total) || total <= 0) return 'default';
  if (used >= total) return 'error';
  if (used >= total - 1) return 'warning';
  return 'info';
}

/* ✅ üye yoklama özeti: kaç pending, kaç alınmış */
function memberAttendanceSummary(rows = []) {
  const list = rows || [];
  if (list.length === 0) return { pending: 0, taken: 0, total: 0 };

  let pending = 0;
  let taken = 0;

  for (const r of list) {
    const st = String(r?.attendanceStatus ?? '').toUpperCase();
    if (st === 'PENDING') pending += 1;
    if (r?.attendanceTakenAt) taken += 1;
  }

  return { pending, taken, total: list.length };
}

function pickMemberKey(r) {
  // backend shape farklı olabilir: memberId varsa onu kullan
  return r?.memberId ?? r?.member?.id ?? r?.participantMemberId ?? null;
}
function rsvpChip(status, reportedAt) {
  const s = String(status ?? '').toUpperCase();
  const hasTime = Boolean(reportedAt);

  if (!s || s === 'UNKNOWN' || s === 'NULL') {
    return { label: 'İşaretlemedi', color: 'default', tip: hasTime ? reportedAt : null };
  }
  if (s === 'YES')
    return { label: 'Geleceğim', color: 'success', tip: hasTime ? reportedAt : null };
  if (s === 'NO')
    return { label: 'Gelmeyeceğim', color: 'error', tip: hasTime ? reportedAt : null };

  return { label: s, color: 'default', tip: hasTime ? reportedAt : null };
}

function rsvpSummary(rows = []) {
  const list = rows || [];
  if (list.length === 0) return { label: '—', color: 'default' };

  let yes = 0,
    no = 0,
    unknown = 0;

  for (const r of list) {
    const s = String(r?.rsvpStatus ?? '').toUpperCase();
    if (!s || s === 'UNKNOWN' || s === 'NULL') unknown += 1;
    else if (s === 'YES') yes += 1;
    else if (s === 'NO') no += 1;
    else unknown += 1;
  }

  if (yes === 0 && no === 0) return { label: 'İşaretlemedi', color: 'default', yes, no, unknown };

  if (yes > 0 && no === 0)
    return { label: `Geleceğim (${yes})`, color: 'success', yes, no, unknown };
  if (no > 0 && yes === 0)
    return { label: `Gelmeyeceğim (${no})`, color: 'error', yes, no, unknown };

  return { label: `Karışık (${yes}/${no})`, color: 'warning', yes, no, unknown };
}

/* ---------------- group by member ---------------- */
function n0(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function groupByMember(rows = []) {
  const map = new Map();

  for (const r of rows || []) {
    const key = r?.memberId ?? (r?.memberName || '').toLowerCase();
    if (!key) continue;

    if (!map.has(key)) {
      map.set(key, {
        memberKey: key,
        memberId: r?.memberId ?? null,
        memberName: r?.memberName ?? '—',
        sessions: [],
      });
    }
    map.get(key).sessions.push(r);
  }

  const members = Array.from(map.values()).map((m) => {
    // ✅ plan tekilleştirme: memberPlanId -> {paid,debt,price, label...}
    const planMap = new Map();

    for (const x of m.sessions || []) {
      const planId = x?.memberPlanId ?? null;
      const planKey = planId ?? `NO_PLAN_${x?.sessionId ?? ''}`;

      if (!planMap.has(planKey)) {
        planMap.set(planKey, {
          planKey,
          planId,
          label: [x?.packageName, x?.packagePeriod].filter(Boolean).join(' • '),
          priceTry: 0,
          paidTry: 0,
          debtTry: 0,
        });
      }

      const p = planMap.get(planKey);

      // ✅ aynı plan satırlarda tekrar ettiği için MAX en güvenlisi
      p.priceTry = Math.max(p.priceTry, n0(x?.planPriceTry));
      p.paidTry = Math.max(p.paidTry, n0(x?.planPaidTry));
      p.debtTry = Math.max(p.debtTry, n0(x?.planDebtTry));
    }

    const plans = Array.from(planMap.values());

    // ✅ artık seans değil, plan toplamı
    const totalPaid = plans.reduce((acc, p) => acc + n0(p.paidTry), 0);
    const totalDebt = plans.reduce((acc, p) => acc + n0(p.debtTry), 0);
    const anyDebt = totalDebt > 0;

    // paket label (tek/çoklu)
    const pkgSet = new Set(plans.map((p) => p.label).filter(Boolean));
    const packageLabel =
      pkgSet.size === 0 ? '—' : pkgSet.size === 1 ? [...pkgSet][0] : `Çoklu (${pkgSet.size})`;

    // son seans
    const last = [...(m.sessions || [])].sort(
      (a, b) => new Date(b.startAt) - new Date(a.startAt)
    )[0];

    const notePreview = (last?.note || '').trim()
      ? last.note.length > 28
        ? last.note.slice(0, 28) + '…'
        : last.note
      : '—';

    return {
      ...m,
      anyDebt,
      totalPaid,
      totalDebt,
      totalSessions: (m.sessions || []).length,
      lastStartAt: last?.startAt ?? null,
      lastActivityName: last?.activityName ?? null,
      packageLabel,
      notePreview,
      attendance: memberAttendanceSummary(m.sessions),
      _plans: plans, // istersen detayda "plan listesi" göstermek için
    };
  });

  members.sort((a, b) => {
    if (a.anyDebt !== b.anyDebt) return a.anyDebt ? -1 : 1;
    return new Date(b.lastStartAt || 0) - new Date(a.lastStartAt || 0);
  });

  return members;
}

/* ---------------- detail table (seanslar) ---------------- */
function DetailsTableHeader({ thSx }) {
  return (
    <MDBox component="thead">
      <MDBox component="tr" sx={{ bgcolor: 'grey.50' }}>
        <MDBox component="th" sx={{ ...thSx, width: 86 }}>
          Tarih
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 58 }}>
          Saat
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 160 }}>
          Aktivite
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 130 }}>
          Seans Türü
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 92 }}>
          Katılım
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 120 }}>
          Üye Beyanı
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 190 }}>
          Paket (Dönem)
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 92 }}>
          Kullanım
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 104 }}>
          Paket Fiyat
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 104 }}>
          Ödenen
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 96 }}>
          Ödeme
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 220 }}>
          Not
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 140 }}>
          Yoklama Zamanı
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 140 }}>
          Alan Kişi
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

function DetailsTableRows({ rows, tdSx }) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const sorted = [...safeRows].sort((a, b) => new Date(b.startAt) - new Date(a.startAt));

  return (
    <MDBox component="tbody">
      {sorted.map((r, i) => {
        const debt = Number(r?.planDebtTry) || 0;
        const isDebt = debt > 0;
        const att = attendanceChip(r.attendanceStatus);

        const rsvp = rsvpChip(r.rsvpStatus, r.rsvpReportedAt);
        const pkg = [safeText(r.packageName, ''), safeText(r.packagePeriod, '')]
          .filter(Boolean)
          .join(' • ');
        const usage = usageLabel(r);

        return (
          <MDBox
            key={r.participantId ?? `${r.sessionId}-${i}`}
            component="tr"
            sx={{
              ...(isDebt && {
                bgcolor: 'error.50',
                borderLeft: '4px solid',
                borderLeftColor: 'error.main',
              }),
              '&:hover': { bgcolor: isDebt ? 'error.100' : 'grey.50' },
            }}
          >
            <MDBox component="td" sx={{ ...tdSx, fontWeight: 800 }}>
              {fmtDateTR(r.startAt)}
            </MDBox>
            <MDBox component="td" sx={tdSx}>
              {fmtTimeTR(r.startAt)}
            </MDBox>

            <MDBox component="td" sx={{ ...tdSx, fontWeight: 800 }}>
              <span title={safeText(r.activityName)}>{short(r.activityName, 24)}</span>
            </MDBox>

            <MDBox component="td" sx={tdSx}>
              <span title={safeText(r.sessionType)}>{short(r.sessionType, 18)}</span>
            </MDBox>

            <MDBox component="td" sx={tdSx}>
              <Chip size="small" variant="outlined" label={att.label} color={att.color} />
            </MDBox>
            <MDBox component="td" sx={tdSx}>
              <span
                title={
                  r.rsvpReportedAt
                    ? `${fmtDateTR(r.rsvpReportedAt)} ${fmtTimeTR(r.rsvpReportedAt)}`
                    : rsvp.label
                }
              >
                <Chip size="small" variant="outlined" label={rsvp.label} color={rsvp.color} />
              </span>
            </MDBox>
            <MDBox component="td" sx={tdSx}>
              <span title={pkg || '—'}>{pkg ? short(pkg, 34) : '—'}</span>
            </MDBox>

            <MDBox component="td" sx={tdSx}>
              {usage ? (
                <Chip size="small" variant="outlined" label={usage} color={usageColor(r)} />
              ) : (
                '—'
              )}
            </MDBox>

            <MDBox component="td" sx={{ ...tdSx, fontWeight: 800 }}>
              {fmtTry(r.planPriceTry)}
            </MDBox>

            <MDBox
              component="td"
              sx={{
                ...tdSx,
                fontWeight: 800,
                color: (Number(r.planPaidTry) || 0) > 0 ? 'success.main' : 'text.primary',
              }}
            >
              {fmtTry(r.planPaidTry)}
            </MDBox>

            <MDBox component="td" sx={tdSx}>
              {paymentLabel(r.paymentMethod)}
            </MDBox>

            <MDBox component="td" sx={tdSx}>
              {safeText(r.note)}
            </MDBox>

            <MDBox component="td" sx={tdSx}>
              {r.attendanceTakenAt ? (
                <>
                  {fmtDateTR(r.attendanceTakenAt)}
                  <br />
                  <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
                    {fmtTimeTR(r.attendanceTakenAt)}
                  </MDTypography>
                </>
              ) : (
                '—'
              )}
            </MDBox>

            <MDBox component="td" sx={tdSx}>
              {safeText(r.attendanceTakenByName)}
            </MDBox>
          </MDBox>
        );
      })}

      {sorted.length === 0 && (
        <MDBox component="tr">
          <MDBox component="td" colSpan={14} sx={{ py: 1.5 }}>
            <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
              Seans bulunamadı.
            </MDTypography>
          </MDBox>
        </MDBox>
      )}
    </MDBox>
  );
}

/* ---------------- summary table (üyeler) ---------------- */
function SummaryHeader({ thSx }) {
  return (
    <MDBox component="thead">
      <MDBox component="tr" sx={{ bgcolor: 'grey.50' }}>
        <MDBox
          component="th"
          sx={{
            ...thSx,
            position: 'sticky',
            left: 0,
            zIndex: 6,
            backgroundColor: 'grey.50',
            boxShadow: '6px 0 8px -8px rgba(0,0,0,0.35)',
          }}
        >
          Üye
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 110 }}>
          Paket
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 92 }}>
          Seans
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 150 }}>
          Yoklama
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 110 }}>
          Ödenen
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 110 }}>
          Borç
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 180 }}>
          Son Seans
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 120 }}>
          Üye Beyanı
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 180 }}>
          Not
        </MDBox>
        <MDBox component="th" sx={{ ...thSx, width: 46, textAlign: 'center' }}>
          {/* expand */}
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

function SummaryRow({ m, tdSx, expanded }) {
  const att = m.attendance || { pending: 0, total: 0 };

  const yoklamaLabel =
    att.total === 0 ? '—' : att.pending > 0 ? `${att.pending} bekliyor` : 'Tamam';
  const yoklamaColor = att.total === 0 ? 'default' : att.pending > 0 ? 'warning' : 'success';
  const rsvp = rsvpSummary(m.sessions);

  const lastLabel = m.lastStartAt
    ? `${fmtDateTR(m.lastStartAt)} • ${fmtTimeTR(m.lastStartAt)}`
    : '—';

  return (
    <MDBox
      component="tr"
      sx={{
        bgcolor: expanded ? 'grey.100' : 'common.white',
        transition: 'background-color 140ms ease',

        ...(m.anyDebt && {
          bgcolor: expanded ? 'error.100' : 'error.50',
        }),

        '&:hover': {
          bgcolor: m.anyDebt ? 'error.100' : 'grey.50',
        },
      }}
    >
      <MDBox
        component="td"
        sx={{
          ...tdSx,
          fontWeight: 900,
          position: 'sticky',
          left: 0,
          zIndex: 5,
          backgroundColor: m.anyDebt
            ? expanded
              ? 'error.100'
              : 'error.50'
            : expanded
            ? 'grey.100'
            : 'common.white',
          boxShadow: '6px 0 8px -8px rgba(0,0,0,0.35)',

          ...(m.anyDebt && {
            pl: 1.25,
            '&:before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 6,
              borderTopLeftRadius: 8,
              borderBottomLeftRadius: 8,
              backgroundColor: 'error.main',
            },
          }),
        }}
      >
        <span title={safeText(m.memberName)}>{short(m.memberName, 26)}</span>
      </MDBox>

      <MDBox component="td" sx={tdSx}>
        <span title={safeText(m.packageLabel)}>{short(m.packageLabel, 18)}</span>
      </MDBox>

      <MDBox component="td" sx={tdSx}>
        <Chip size="small" variant="outlined" label={`${m.totalSessions} seans`} />
      </MDBox>

      <MDBox component="td" sx={tdSx}>
        <Chip size="small" variant="outlined" label={yoklamaLabel} color={yoklamaColor} />
      </MDBox>

      <MDBox component="td" sx={{ ...tdSx, fontWeight: 800 }}>
        {fmtTry(m.totalPaid)}
      </MDBox>

      <MDBox
        component="td"
        sx={{
          ...tdSx,
          fontWeight: 900,
          color: m.totalDebt > 0 ? 'error.main' : 'success.main',
        }}
      >
        {fmtTry(m.totalDebt)}
      </MDBox>

      <MDBox component="td" sx={tdSx}>
        <span title={safeText(m.lastActivityName)}>{lastLabel}</span>
      </MDBox>
      <MDBox component="td" sx={tdSx}>
        <span title={`YES:${rsvp.yes ?? 0}  NO:${rsvp.no ?? 0}  UNK:${rsvp.unknown ?? 0}`}>
          <Chip size="small" variant="outlined" label={rsvp.label} color={rsvp.color} />
        </span>
      </MDBox>
      <MDBox component="td" sx={tdSx}>
        <span title={safeText(m.notePreview)}>{m.notePreview}</span>
      </MDBox>

      <MDBox component="td" sx={{ ...tdSx, textAlign: 'center' }}>
        {/* icon column */}
      </MDBox>
    </MDBox>
  );
}

/* ---------------- main ---------------- */
export function StaffMembersAccordion({ rows = [] }) {
  const members = useMemo(() => groupByMember(rows), [rows]);
  const [open, setOpen] = useState(() => new Set());

  const toggle = (memberKey) => {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(memberKey)) next.delete(memberKey);
      else next.add(memberKey);
      return next;
    });
  };

  const thSx = {
    textAlign: 'left',
    px: 0.75,
    py: 0.6,
    fontSize: 10.25,
    fontWeight: 900,
    borderBottom: '1px solid',
    borderColor: 'grey.200',
    whiteSpace: 'nowrap',
  };
  const tdSx = {
    px: 0.75,
    py: 0.45,
    fontSize: 10.75,
    borderBottom: '1px solid',
    borderColor: 'grey.100',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <MDBox>
      <MDBox sx={{ overflowX: 'auto' }}>
        <MDBox
          component="table"
          sx={{
            width: '100%',
            minWidth: 1250,
            borderCollapse: 'separate',
            borderSpacing: 0,
            tableLayout: 'fixed',
          }}
        >
          <ColGroup cols={SUMMARY_COLS} />
          <SummaryHeader thSx={thSx} />

          <MDBox component="tbody">
            {members.map((m) => {
              const expanded = open.has(m.memberKey);

              return (
                <MDBox key={String(m.memberKey)} component="tr">
                  <MDBox
                    component="td"
                    colSpan={10}
                    sx={{ p: 0, borderBottom: '1px solid', borderColor: 'grey.100' }}
                  >
                    <Accordion
                      expanded={expanded}
                      onChange={() => toggle(m.memberKey)}
                      disableGutters
                      elevation={0}
                      square
                      sx={{
                        bgcolor: 'transparent',
                        '&:before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <Icon sx={{ fontSize: 18 }}>
                            {expanded ? 'expand_less' : 'expand_more'}
                          </Icon>
                        }
                        sx={{
                          p: 0,
                          minHeight: 42,
                          '& .MuiAccordionSummary-content': { m: 0, p: 0 },
                          '& .MuiAccordionSummary-contentGutters': { m: 0 },
                          '& .MuiAccordionSummary-expandIconWrapper': { mr: 0.75, opacity: 0.75 },
                          cursor: 'pointer',
                        }}
                      >
                        <MDBox
                          component="table"
                          sx={{
                            width: '100%',
                            borderCollapse: 'separate',
                            borderSpacing: 0,
                            tableLayout: 'fixed',
                          }}
                        >
                          <ColGroup cols={SUMMARY_COLS} />
                          <MDBox component="tbody">
                            <SummaryRow m={m} tdSx={tdSx} expanded={expanded} />
                          </MDBox>
                        </MDBox>
                      </AccordionSummary>

                      <AccordionDetails
                        sx={{
                          pt: 1,
                          pb: 1.25,
                          bgcolor: 'grey.25',
                          borderTop: '1px solid',
                          borderColor: 'grey.100',
                        }}
                      >
                        <MDBox
                          sx={{
                            overflowX: 'auto',
                            maxHeight: 360,
                            overflowY: 'auto',
                            borderRadius: 1.25,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            bgcolor: 'common.white',
                          }}
                        >
                          <MDBox
                            component="table"
                            sx={{
                              width: '100%',
                              minWidth: 1520,
                              borderCollapse: 'collapse',
                              tableLayout: 'fixed',
                            }}
                          >
                            <DetailsTableHeader thSx={thSx} />
                            <DetailsTableRows rows={m.sessions} tdSx={tdSx} />
                          </MDBox>
                        </MDBox>
                      </AccordionDetails>
                    </Accordion>
                  </MDBox>
                </MDBox>
              );
            })}

            {members.length === 0 && (
              <MDBox component="tr">
                <MDBox component="td" colSpan={10} sx={{ py: 2 }}>
                  <MDTypography variant="caption" sx={{ opacity: 0.7 }}>
                    Üye bulunamadı.
                  </MDTypography>
                </MDBox>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}
